import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Paper, Typography } from "@material-ui/core";
import ValidateStep from "./ValidateStep";
import IntakeFormStep from "./IntakeFormStep";
import SubmittedStep from "./SubmittedStep";
import _ from "lodash";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

const DEFAULT_LOGO = "https://porton-shared.s3.ca-central-1.amazonaws.com/images/Porton_New_Logo.png";

const useStyles = makeStyles((theme) => ({
    errorRoot: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F6FA",
        padding: theme.spacing(2),
    },
    errorCard: {
        padding: theme.spacing(4),
        maxWidth: 420,
        width: "100%",
        textAlign: "center",
        borderRadius: 8,
    },
    errorTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    errorBody: {
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
}));

function ErrorPanel({ title, body }) {
    const classes = useStyles();
    return (
        <Box className={classes.errorRoot}>
            <Paper elevation={2} className={classes.errorCard}>
                <Typography variant="h6" className={classes.errorTitle}>{title}</Typography>
                <Typography className={classes.errorBody}>{body}</Typography>
            </Paper>
        </Box>
    );
}

export default function IntakeFormPage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");
    const orgId = params.get("o");

    const [step, setStep] = useState("validate"); // "validate" | "form" | "submitted" | "expired"
    const [validated, setValidated] = useState(null); // { hin, dob, token, orgId }
    const [expiredMsg, setExpiredMsg] = useState("");

    const [logo, setLogo] = useState(DEFAULT_LOGO);
    const [orgInfo, setOrgInfo] = useState({ address: "", contactNum: "" });

    useEffect(() => {
        if (!orgId) return;
        const fetchOrg = async () => {
            try {
                const result = await api(apiConfigs.baseOption("get", `orgs/${orgId}/getReport`));
                if (result.status === 200) {
                    const orgLogo = _.get(result, "data.logo", DEFAULT_LOGO);
                    if (orgLogo) setLogo(orgLogo);
                    setOrgInfo({
                        address: _.get(result, "data.address", ""),
                        contactNum: _.get(result, "data.contactNum", ""),
                    });
                }
            } catch (err) {
                // fail silently — page still works without org branding
            }
        };
        fetchOrg();
    }, [orgId]);

    if (!token) {
        return (
            <ErrorPanel
                title="Invalid link"
                body="Please use the link provided in your email. If you believe this is an error, contact your clinic."
            />
        );
    }

    if (step === "expired") {
        return (
            <ErrorPanel
                title="Link unavailable"
                body={expiredMsg || "This form has already been submitted or the link is no longer valid. Please contact your clinic if you need to make changes."}
            />
        );
    }

    if (step === "submitted") {
        return <SubmittedStep logo={logo} orgInfo={orgInfo} />;
    }

    if (step === "form" && validated) {
        return (
            <IntakeFormStep
                token={validated.token}
                orgId={validated.orgId}
                hin={validated.hin}
                dob={validated.dob}
                logo={logo}
                orgInfo={orgInfo}
                onSubmitted={() => setStep("submitted")}
            />
        );
    }

    return (
        <ValidateStep
            token={token}
            orgId={orgId}
            logo={logo}
            orgInfo={orgInfo}
            onValidated={(data) => {
                setValidated(data);
                setStep("form");
            }}
            onExpired={(msg) => {
                setExpiredMsg(msg);
                setStep("expired");
            }}
        />
    );
}
